import { createContext, createElement, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams, type LinkProps } from 'react-router-dom'
import { READINESS_FEATURES, isObjectiveRoute, type FeatureKey } from '@/lib/readiness/module'

// ── Origin-aware navigation (Iteration 5 §4, Note 1) ────────────────────────
// One origin value drives the back link, the active strip tab and the
// breadcrumb of a detail page. It travels in router state as `returnTo`,
// mirrored as `?from={feature}` so a reload keeps the feature. `history.back()`
// is never used: going back navigates to the origin route with a `restore`
// payload (scroll position, clicked element, view state) the origin page
// applies on mount. Chains unwind in order because each page provides its own
// origin to the links it renders.

export interface Crumb {
  label: string
  to: string
}

export interface Origin {
  feature: FeatureKey // active strip tab
  label: string // back link text
  to: string // back link target (route + query)
  trail: Crumb[] // breadcrumb segments after `Dashboard`
  // The origin page's own router state, so a chain unwinds in order: back from
  // the catalogue entry restores TE-2 *with* TE-2's own origin (Progress).
  state?: NavState | null
}

export interface RestoreState {
  scrollY: number
  focusKey: string | null
  viewState?: unknown
}

export interface ReturnTo {
  origin: Origin
  restore: RestoreState
}

interface NavState {
  returnTo?: ReturnTo
  restore?: RestoreState
}

export const FEATURE_LABEL: Record<FeatureKey, string> = { dashboard: 'Dashboard', progress: 'Progress', submission: 'CSA Submission', tracker: 'Migration Tracker', catalogue: 'Technology Catalogue', settings: 'Settings' }

export function featureOrigin(feature: FeatureKey, indexId: string): Origin {
  const f = READINESS_FEATURES.find((x) => x.key === feature) ?? READINESS_FEATURES[0]
  const to = `${f.route}?index=${indexId}`
  return { feature: f.key, label: f.label, to, trail: f.key === 'dashboard' ? [] : [{ label: f.label, to }] }
}

const isFeatureKey = (s: string | null): s is FeatureKey => !!s && READINESS_FEATURES.some((f) => f.key === s)

// Detail routes: the strip tab, breadcrumb and back link follow the origin.
// Each has a fallback origin for a direct load, deep link or new tab.
export function detailFallback(pathname: string): FeatureKey | null {
  if (isObjectiveRoute(pathname)) return 'dashboard'
  if (/^\/readiness\/report\/live\/?$/.test(pathname)) return 'dashboard'
  if (pathname.startsWith('/readiness/report/')) return 'progress'
  if (/^\/readiness\/catalogue\/[^/]+/.test(pathname)) return 'catalogue'
  return null
}

export function useOrigin(): Origin {
  const location = useLocation()
  const [params] = useSearchParams()
  const indexId = params.get('index') ?? 'csa-qri-v1'
  const state = location.state as NavState | null
  const from = params.get('from')
  return useMemo(() => {
    if (state?.returnTo?.origin) return state.returnTo.origin
    if (isFeatureKey(from)) return featureOrigin(from, indexId)
    const fb = detailFallback(location.pathname)
    return featureOrigin(fb ?? 'dashboard', indexId)
  }, [state, from, indexId, location.pathname])
}

// The feature the strip and drawer highlight: a detail route follows its
// origin; a feature route is itself.
export function useActiveFeature(): FeatureKey {
  const { pathname } = useLocation()
  const origin = useOrigin()
  if (detailFallback(pathname)) return origin.feature
  const f = READINESS_FEATURES.find((x) => x.match(pathname))
  return f?.key ?? 'dashboard'
}

// ── Providing an origin to the links on a page ──────────────────────────────

interface OriginCtx {
  origin: Origin
  getViewState?: () => unknown
}

const OriginContext = createContext<OriginCtx | null>(null)

export function OriginProvider({ origin, getViewState, children }: { origin: Origin; getViewState?: () => unknown; children: ReactNode }) {
  const value = useMemo(() => ({ origin, getViewState }), [origin, getViewState])
  return createElement(OriginContext.Provider, { value }, children)
}

// A feature page's own origin: label and route of the feature.
export function useFeatureOrigin(feature: FeatureKey): Origin {
  const [params] = useSearchParams()
  const indexId = params.get('index') ?? 'csa-qri-v1'
  return useMemo(() => featureOrigin(feature, indexId), [feature, indexId])
}

// A detail page's origin for the links it renders: its own origin's feature
// and trail, plus itself (so Progress → TE-2 → catalogue entry unwinds in order).
export function useChainedOrigin(label: string): Origin {
  const location = useLocation()
  const origin = useOrigin()
  const here = `${location.pathname}${location.search}`
  return useMemo(() => ({ feature: origin.feature, label, to: here, trail: [...origin.trail, { label, to: here }] }), [origin, label, here])
}

const withFrom = (to: string, feature: FeatureKey) => {
  const [path, hash = ''] = to.split('#')
  const [p, q = ''] = path.split('?')
  const sp = new URLSearchParams(q)
  sp.set('from', feature)
  return `${p}?${sp.toString()}${hash ? `#${hash}` : ''}`
}

// Builds the link target and state for a navigation away from the current page.
export function useOriginLink() {
  const ctx = useContext(OriginContext)
  const location = useLocation()
  const [params] = useSearchParams()
  const indexId = params.get('index') ?? 'csa-qri-v1'
  return useCallback(
    (to: string, el?: Element | null): { to: string; state: NavState } => {
      const base = ctx?.origin ?? featureOrigin(activeFromPath(location.pathname), indexId)
      const origin: Origin = { ...base, state: (location.state as NavState | null) ?? null }
      const focusKey = (el as HTMLElement | null)?.dataset?.originKey ?? to
      return { to: withFrom(to, origin.feature), state: { returnTo: { origin, restore: { scrollY: window.scrollY, focusKey, viewState: ctx?.getViewState?.() } } } }
    },
    [ctx, location.pathname, location.state, indexId]
  )
}

function activeFromPath(pathname: string): FeatureKey {
  const f = READINESS_FEATURES.find((x) => x.match(pathname))
  return f?.key ?? 'dashboard'
}

export function useOriginNavigate() {
  const navigate = useNavigate()
  const link = useOriginLink()
  return useCallback((to: string, el?: Element | null) => { const l = link(to, el); navigate(l.to, { state: l.state }) }, [navigate, link])
}

// Link that carries the page's origin. `data-origin-key` marks the element the
// focus ring returns to; defaults to the target. Forwards its ref so a tooltip
// can anchor to it.
export const OriginLink = forwardRef<HTMLAnchorElement, { to: string; children: ReactNode; originKey?: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>>(function OriginLink({ to, children, onClick, originKey, ...rest }, ref) {
  const link = useOriginLink()
  const navigate = useNavigate()
  const key = originKey ?? to
  const l = link(to)
  const props = {
    ...rest,
    ref,
    to: l.to,
    state: l.state,
    'data-origin-key': key,
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e)
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
      e.preventDefault()
      const fresh = link(to, e.currentTarget)
      navigate(fresh.to, { state: fresh.state })
    },
  } as LinkProps
  return createElement(Link, props, children)
})

// ── Going back and restoring the spot ───────────────────────────────────────

export function useBack(): { origin: Origin; back: () => void } {
  const navigate = useNavigate()
  const location = useLocation()
  const origin = useOrigin()
  const state = location.state as NavState | null
  const back = useCallback(() => {
    navigate(origin.to, { state: { ...(origin.state ?? {}), restore: state?.returnTo?.restore ?? null } })
  }, [navigate, origin.to, origin.state, state])
  return { origin, back }
}

// Origin pages call this on mount: returns the saved view state (if any) and
// restores scroll + a brief focus ring on the element the user clicked.
export function useRestoreOnReturn<T = unknown>(): T | null {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as NavState | null
  const restore = state?.restore ?? null
  const done = useRef(false)
  useEffect(() => {
    // Applied once per arrival (StrictMode mounts effects twice in development;
    // the timers are not cancelled so the second mount cannot undo the first).
    if (!restore || done.current) return
    done.current = true
    const apply = () => {
      window.scrollTo({ top: restore.scrollY, behavior: 'auto' })
      if (restore.focusKey) {
        const el = document.querySelector<HTMLElement>(`[data-origin-key="${CSS.escape(restore.focusKey)}"]`)
        if (el) {
          el.classList.add('rd-return-focus')
          el.focus({ preventScroll: true })
          window.setTimeout(() => el.classList.remove('rd-return-focus'), 1600)
        }
      }
    }
    window.requestAnimationFrame(() => { apply(); window.setTimeout(apply, 80) })
    // Clear the payload so a reload lands at the top like a fresh visit.
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (restore?.viewState as T | undefined) ?? null
}
